import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import Footer from '@/components/feature/Footer';

export default function Register() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const redirectQuery = redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password.trim() || !confirmPassword.trim() || !nickname.trim()) {
      setError('모든 필드를 입력해주세요.');
      return;
    }

    if (password.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    if (password !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (nickname.trim().length < 2) {
      setError('닉네임은 최소 2자 이상이어야 합니다.');
      return;
    }

    setSubmitting(true);
    const { error: signUpError } = await signUp(email.trim(), password, nickname.trim());
    setSubmitting(false);

    if (signUpError) {
      setError(signUpError);
      return;
    }

    setSuccess(true);
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <header className="bg-secondary-950 text-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center h-14 md:h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-500 rounded flex items-center justify-center">
                  <span className="text-white font-bold text-sm">스</span>
                </div>
                <span className="text-xl font-bold text-primary-500">스카연</span>
              </Link>
            </div>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-lg p-8 border border-foreground-200 text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-check-line text-2xl text-primary-500" />
              </div>
              <h1 className="text-2xl font-bold text-foreground-950 mb-2">회원가입 신청 완료</h1>
              <p className="text-sm text-foreground-600 mb-6">
                {email}로 인증 메일을 발송했습니다.<br />
                이메일을 확인하여 인증을 완료해주세요.
              </p>
              <div className="space-y-3">
                <Link
                  to={`/login${redirectQuery}`}
                  className="block w-full bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm py-3 rounded-md transition-colors whitespace-nowrap"
                >
                  로그인 페이지로 이동
                </Link>
                <Link
                  to="/"
                  className="block text-sm text-foreground-600 hover:text-primary-500 transition-colors"
                >
                  메인으로 돌아가기
                </Link>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <header className="bg-secondary-950 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center h-14 md:h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary-500 rounded flex items-center justify-center">
                <span className="text-white font-bold text-sm">스</span>
              </div>
              <span className="text-xl font-bold text-primary-500">스카연</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg p-8 border border-foreground-200">
            <h1 className="text-2xl font-bold text-foreground-950 text-center mb-2">회원가입</h1>
            <p className="text-sm text-foreground-600 text-center mb-8">
              스카연 커뮤니티에 가입하고 모든 기능을 이용하세요
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                    이메일
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    className="w-full px-4 py-3 text-sm rounded-md border border-foreground-200 bg-white text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label htmlFor="nickname" className="block text-sm font-medium text-foreground-700 mb-1">
                    닉네임
                  </label>
                  <input
                    id="nickname"
                    name="nickname"
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="커뮤니티에서 사용할 닉네임"
                    className="w-full px-4 py-3 text-sm rounded-md border border-foreground-200 bg-white text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                    autoComplete="username"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1">
                    비밀번호
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="최소 6자 이상"
                    className="w-full px-4 py-3 text-sm rounded-md border border-foreground-200 bg-white text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                    autoComplete="new-password"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground-700 mb-1">
                    비밀번호 확인
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="비밀번호를 다시 입력하세요"
                    className="w-full px-4 py-3 text-sm rounded-md border border-foreground-200 bg-white text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                    autoComplete="new-password"
                  />
                </div>

                {error && (
                  <div className="bg-primary-50 border border-primary-200 text-primary-700 text-sm px-4 py-3 rounded-md">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium text-sm py-3 rounded-md transition-colors whitespace-nowrap"
                >
                  {submitting ? '가입 중...' : '회원가입'}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-foreground-600">
                이미 계정이 있으신가요?{' '}
                <Link to={`/login${redirectQuery}`} className="text-primary-500 hover:text-primary-600 font-medium transition-colors">
                  로그인
                </Link>
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-foreground-200">
              <Link
                to="/"
                className="block text-center text-sm text-foreground-600 hover:text-primary-500 transition-colors"
              >
                <i className="ri-arrow-left-line mr-1" />
                메인으로 돌아가기
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}